import {EmployeeShort} from "../../models/users/Employee";
import {makeStyles} from "@mui/styles";
import {userAvatarStyles} from "../../config/Theme";
import React, {CSSProperties} from "react";
import {ClassStylable} from "../../models/common/ComponentBehavioural";

/**
 * Defines the properties required to render an employee's avatar,
 * with variability in size and customizable styles.
 *
 * This interface is typically used in components responsible for displaying
 * an employee's profile photo or avatar in various sections of an application,
 * such as dashboards or user details pages.
 *
 * @interface EmployeeAvatarProps
 * @extends ClassStylable - Extends styling-related properties for custom class names.
 *
 * @property variant - Optional. Determines the visual size of the avatar.
 *                     Accepts "small" for a compact version or "large" for a more prominent display.
 *                     Defaults to a standard size if not specified.
 * @property employee - Mandatory. Contains core information about the employee to render the avatar,
 *                      such as their photo URL or name, defined by the `EmployeeShort` type.
 * @property style - Optional. Custom inline styles for additional UI configurations
 *                   (e.g., margins, borders, or shape alterations).
 */
export interface EmployeeAvatarProps extends ClassStylable {
    variant?: "small" | "medium" | "large"
    employee: EmployeeShort
    style?: CSSProperties
}

/**
 * A hook for generating dynamic styling rules based on the current theme.
 *
 * This variable exists to centralize and standardize the styling logic for components, ensuring that
 * they adapt seamlessly to the application's theme. It facilitates consistent and maintainable visuals across the app.
 *
 * The function leverages the `makeStyles` utility to define a set of styling rules by extending
 * base styles (`userAvatarStyles`) with any additional customizations derived from the provided theme.
 *
 * @param theme - The theme object which provides design tokens such as colors, typography, and spacing.
 * @return A styles object containing CSS classes that can be applied to components.
 * @throws Will throw an error if `makeStyles` encounters an invalid theme object or if `userAvatarStyles`
 * generates invalid styles.
 */
const useStyle = makeStyles(theme => ({
    ...userAvatarStyles(theme)
}));

/**
 * Renders an avatar representation of an employee for display purposes.
 *
 * This method provides a visual representation of an employee based on size
 * and style customization. It supports rendering in different sizes and helps
 * customize its appearance using provided styles and class names.
 *
 * @param variant - Specifies the size of the avatar. Acceptable values are `"large"` or other variations for default size.
 * @param employee - An object representing the employee. It must include a `shorthandName` property that is used for display.
 * @param style - An optional inline style object to customize the appearance of the avatar.
 * @param className - An optional additional CSS class name to apply for custom styling.
 * @return The JSX element representing the employee's avatar, styled according to the given parameters.
 * @throws Error if `shorthandName` is not defined on the `employee` object.
 */
export function EmployeeAvatar({ variant, employee, style, className }: EmployeeAvatarProps) {
    const classes = useStyle();

    if( variant === "small" ) {
        return (
            <div className={ classes.userAvatarSmall +" "+ (className || "") } style={style}>
                { employee.shorthandName }
            </div>
        );

    } else if( variant === "medium" ) {
        return (
            <div className={ classes.userAvatarMedium +" "+ (className || "") } style={style}>
                { employee.shorthandName }
            </div>
        );
    } else {
        return (
            <div className={ classes.userAvatar +" "+ (className || "") } style={style}>
                { employee.shorthandName }
            </div>
        );
    }

}